import { APIClient, TAPIClient, type TLanguageService, type TRequestIdService } from './client';
import { AccessTokensClient, TAccessTokensClient } from './clients/access-tokens';
import { ActionsClient, TActionsClient } from './clients/actions';
import { AreasClient, TAreasClient } from './clients/areas';
import { AuthClient, TAuthClient, type TDeviceInfoService } from './clients/auth';
import { ClientsClient, TClientsClient } from './clients/clients';
import { CompaniesClient, TCompaniesClient } from './clients/companies';
import { ConfigsClient, TConfigsClient } from './clients/configs';
import { ContactsClient, TContactsClient } from './clients/contacts';
import { DeviceProfileActionsClient, TDeviceProfileActionsClient } from './clients/device-profile-actions';
import { DeviceProfileConfigsClient, TDeviceProfileConfigsClient } from './clients/device-profile-configs';
import { DeviceProfileErrorsClient, TDeviceProfileErrorsClient } from './clients/device-profile-errors';
import { DeviceProfileEventsClient, TDeviceProfileEventsClient } from './clients/device-profile-events';
import { DeviceProfileMetricsClient, TDeviceProfileMetricsClient } from './clients/device-profile-metrics';
import { DeviceProfileRevisionsClient, TDeviceProfileRevisionsClient } from './clients/device-profile-revisions';
import { DeviceProfileStatesClient, TDeviceProfileStatesClient } from './clients/device-profile-states';
import { DeviceProfilesClient, TDeviceProfilesClient } from './clients/device-profiles';
import { DeviceTypesClient, TDeviceTypesClient } from './clients/device-types';
import { DevicesClient, TDevicesClient } from './clients/devices';
import { ErrorsClient, TErrorsClient } from './clients/errors';
import { EventsClient, TEventsClient } from './clients/events';
import { FlowActionsClient, TFlowActionsClient } from './clients/flow-actions';
import { FlowTriggersClient, TFlowTriggersClient } from './clients/flow-triggers';
import { FlowsClient, TFlowsClient } from './clients/flows';
import { GatewaysClient, TGatewaysClient } from './clients/gateways';
import { InputWebhooksClient, TInputWebhooksClient } from './clients/input-webhooks';
import { LoginLogsClient, TLoginLogsClient } from './clients/login-logs';
import { MetricsClient, TMetricsClient } from './clients/metrics';
import { MulticastGroupsClient, TMulticastGroupsClient } from './clients/multicast-groups';
import { PassportizationClient, TPassportizationClient } from './clients/passportization';
import { PermissionsClient, TPermissionsClient } from './clients/permissions';
import { PlansClient, TPlansClient } from './clients/plans';
import { RefreshTokensClient, TRefreshTokensClient } from './clients/refresh-tokens';
import { ReportsClient, TReportsClient } from './clients/reports';
import { ResourcesClient, TResourcesClient } from './clients/resources';
import { RolesClient, TRolesClient } from './clients/roles';
import { StatesClient, TStatesClient } from './clients/states';
import { SystemClient, TSystemClient } from './clients/system';
import { UsersClient, TUsersClient } from './clients/users';

type TApiSDKOptions = {
  host: string;
  pathPrefix: string;
  clientId: string;
  clientSecret: string;
};

type TApiSDK = {
  // set auth token
  setAuthToken(token: string): void;

  // clients
  accessTokens: TAccessTokensClient;
  actions: TActionsClient;
  areas: TAreasClient;
  auth: TAuthClient;
  clients: TClientsClient;
  companies: TCompaniesClient;
  configs: TConfigsClient;
  contacts: TContactsClient;
  errors: TErrorsClient;
  events: TEventsClient;
  flows: TFlowsClient;
  flowActions: TFlowActionsClient;
  flowTriggers: TFlowTriggersClient;
  inputWebhooks: TInputWebhooksClient;
  metrics: TMetricsClient;
  deviceProfiles: TDeviceProfilesClient;
  deviceProfileRevisions: TDeviceProfileRevisionsClient;
  deviceProfileActions: TDeviceProfileActionsClient;
  deviceProfileConfigs: TDeviceProfileConfigsClient;
  deviceProfileErrors: TDeviceProfileErrorsClient;
  deviceProfileEvents: TDeviceProfileEventsClient;
  deviceProfileMetrics: TDeviceProfileMetricsClient;
  deviceProfileStates: TDeviceProfileStatesClient;
  states: TStatesClient;
  deviceTypes: TDeviceTypesClient;
  devices: TDevicesClient;
  gateways: TGatewaysClient;
  loginLogs: TLoginLogsClient;
  multicastGroups: TMulticastGroupsClient;
  passportization: TPassportizationClient;
  permissions: TPermissionsClient;
  plans: TPlansClient;
  refreshTokens: TRefreshTokensClient;
  resources: TResourcesClient;
  reports: TReportsClient;
  roles: TRolesClient;
  system: TSystemClient;
  users: TUsersClient;
};

function ApiSDK(
  options: TApiSDKOptions,
  deviceInfoService: TDeviceInfoService,
  requestIdService: TRequestIdService,
  languageService: TLanguageService
): TApiSDK {
  const authClient: TAuthClient = AuthClient(options, deviceInfoService);

  const apiClient: TAPIClient = APIClient(options, requestIdService, languageService);

  const accessTokensClient: TAccessTokensClient = AccessTokensClient(apiClient);
  const actionsClient: TActionsClient = ActionsClient(apiClient);
  const areasClient: TAreasClient = AreasClient(apiClient);
  const clientsClient: TClientsClient = ClientsClient(apiClient);
  const companiesClient: TCompaniesClient = CompaniesClient(apiClient);
  const configsClient: TConfigsClient = ConfigsClient(apiClient);
  const contactsClient: TContactsClient = ContactsClient(apiClient);
  const deviceProfilesClient: TDeviceProfilesClient = DeviceProfilesClient(apiClient);
  const deviceProfileRevisionsClient: TDeviceProfileRevisionsClient = DeviceProfileRevisionsClient(apiClient);
  const deviceProfileActionsClient: TDeviceProfileActionsClient = DeviceProfileActionsClient(apiClient);
  const deviceProfileConfigsClient: TDeviceProfileConfigsClient = DeviceProfileConfigsClient(apiClient);
  const deviceProfileErrorsClient: TDeviceProfileErrorsClient = DeviceProfileErrorsClient(apiClient);
  const deviceProfileEventsClient: TDeviceProfileEventsClient = DeviceProfileEventsClient(apiClient);
  const deviceProfileMetricsClient: TDeviceProfileMetricsClient = DeviceProfileMetricsClient(apiClient);
  const deviceProfileStatesClient: TDeviceProfileStatesClient = DeviceProfileStatesClient(apiClient);
  const errorsClient: TErrorsClient = ErrorsClient(apiClient);
  const eventsClient: TEventsClient = EventsClient(apiClient);
  const flowsClient: TFlowsClient = FlowsClient(apiClient);
  const flowActionsClient: TFlowActionsClient = FlowActionsClient(apiClient);
  const flowTriggersClient: TFlowTriggersClient = FlowTriggersClient(apiClient);
  const metricsClient: TMetricsClient = MetricsClient(apiClient);
  const deviceTypesClient: TDeviceTypesClient = DeviceTypesClient(apiClient);
  const devicesClient: TDevicesClient = DevicesClient(apiClient);
  const gatewaysClient: TGatewaysClient = GatewaysClient(apiClient);
  const inputWebhooksClient: TInputWebhooksClient = InputWebhooksClient(apiClient);
  const loginLogsClient: TLoginLogsClient = LoginLogsClient(apiClient);
  const multicastGroupsClient: TMulticastGroupsClient = MulticastGroupsClient(apiClient);
  const passportizationClient: TPassportizationClient = PassportizationClient(apiClient);
  const permissionsClient: TPermissionsClient = PermissionsClient(apiClient);
  const plansClient: TPlansClient = PlansClient(apiClient);
  const refreshTokensClient: TRefreshTokensClient = RefreshTokensClient(apiClient);
  const resourcesClient: TResourcesClient = ResourcesClient(apiClient);
  const reportsClient: TReportsClient = ReportsClient(apiClient);
  const rolesClient: TRolesClient = RolesClient(apiClient);
  const systemClient: TSystemClient = SystemClient(apiClient);
  const usersClient: TUsersClient = UsersClient(apiClient);
  const statesClient: TStatesClient = StatesClient(apiClient);

  return {
    // set auth token
    setAuthToken(token: string): void {
      apiClient.setAuthToken(token);
    },

    // clients
    accessTokens: accessTokensClient,
    areas: areasClient,
    auth: authClient,
    clients: clientsClient,
    companies: companiesClient,
    configs: configsClient,
    contacts: contactsClient,
    actions: actionsClient,
    errors: errorsClient,
    events: eventsClient,
    flows: flowsClient,
    flowActions: flowActionsClient,
    flowTriggers: flowTriggersClient,
    inputWebhooks: inputWebhooksClient,
    metrics: metricsClient,
    deviceProfiles: deviceProfilesClient,
    states: statesClient,
    deviceTypes: deviceTypesClient,
    deviceProfileRevisions: deviceProfileRevisionsClient,
    deviceProfileActions: deviceProfileActionsClient,
    deviceProfileConfigs: deviceProfileConfigsClient,
    deviceProfileErrors: deviceProfileErrorsClient,
    deviceProfileEvents: deviceProfileEventsClient,
    deviceProfileMetrics: deviceProfileMetricsClient,
    deviceProfileStates: deviceProfileStatesClient,
    devices: devicesClient,
    gateways: gatewaysClient,
    loginLogs: loginLogsClient,
    multicastGroups: multicastGroupsClient,
    passportization: passportizationClient,
    permissions: permissionsClient,
    plans: plansClient,
    refreshTokens: refreshTokensClient,
    resources: resourcesClient,
    reports: reportsClient,
    roles: rolesClient,
    system: systemClient,
    users: usersClient,
  };
}

export { ApiSDK };
export type { TApiSDKOptions, TApiSDK };
